import { useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { Lock, Eye, EyeOff, ShieldCheck, ArrowRight, Loader2, AlertCircle, CheckCircle2 } from "lucide-react";
import { resetPassword } from "../api/auth";

export default function ResetPassword() {
  const [searchParams] = useSearchParams();
  const token = searchParams.get("token");

  const [form, setForm] = useState({ password: "", confirm: "" });
  const [show, setShow] = useState({ password: false, confirm: false });
  const [errors, setErrors] = useState({});
  const [apiError, setApiError] = useState("");
  const [success, setSuccess] = useState(false);
  const [loading, setLoading] = useState(false);

  const validate = () => {
    const temp = {};
    if (!form.password) {
      temp.password = "Password is required";
    } else if (form.password.length < 6) {
      temp.password = "Must be at least 6 characters";
    }
    if (!form.confirm) {
      temp.confirm = "Please confirm your password";
    } else if (form.password && form.confirm !== form.password) {
      temp.confirm = "Passwords do not match";
    }
    setErrors(temp);
    return Object.keys(temp).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setApiError("");
    if (!token) {
      setApiError("This reset link is missing its token. Please request a new one.");
      return;
    }
    if (!validate()) return;

    try {
      setLoading(true);
      await resetPassword(token, form.password, form.confirm);
      setSuccess(true);
    } catch (error) {
      setApiError(
        error.response?.data?.message || "This reset link is invalid or has expired. Please request a new one."
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-100 flex items-center justify-center p-4 sm:p-10">
      <div className="w-full max-w-md">
        <div className="bg-white rounded-3xl border border-slate-200 shadow-2xl p-6 sm:p-10">
          <div className="flex justify-center">
            <div className="w-16 h-16 rounded-2xl bg-blue-600 flex items-center justify-center shadow-lg">
              <ShieldCheck size={30} className="text-white" />
            </div>
          </div>

          <h2 className="mt-6 text-center text-3xl font-black text-slate-900">Reset Password</h2>
          <p className="text-center text-slate-500 mt-3">Choose a new password for your account</p>

          <div className="mt-10 space-y-6">
            {apiError && (
              <div className="flex items-start gap-3 bg-red-50 border border-red-200 text-red-700 p-4 rounded-xl">
                <AlertCircle size={20} className="text-red-500 shrink-0 mt-0.5" />
                <div className="text-sm font-medium">{apiError}</div>
              </div>
            )}

            {success ? (
              <div className="space-y-6">
                <div className="flex items-start gap-3 bg-emerald-50 border border-emerald-200 text-emerald-700 p-4 rounded-xl">
                  <CheckCircle2 size={20} className="text-emerald-500 shrink-0 mt-0.5" />
                  <div className="text-sm font-medium">
                    Your password has been reset successfully. You can now sign in with your new password.
                  </div>
                </div>
                <Link
                  to="/"
                  className="w-full bg-blue-600 hover:bg-blue-700 transition-all duration-200 text-white rounded-xl py-3.5 font-semibold shadow-lg shadow-blue-200 flex justify-center items-center gap-3"
                >
                  Go to Sign In <ArrowRight size={18} />
                </Link>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="space-y-6">
                <div>
                  <label className="block mb-2 text-sm font-semibold text-slate-700">New Password</label>
                  <div className={`relative rounded-xl border transition-all ${errors.password ? "border-red-400 ring-2 ring-red-100" : "border-slate-300 focus-within:border-blue-600 focus-within:ring-4 focus-within:ring-blue-100"}`}>
                    <Lock size={18} className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400" />
                    <input
                      type={show.password ? "text" : "password"}
                      placeholder="Enter new password"
                      value={form.password}
                      onChange={(e) => setForm({ ...form, password: e.target.value })}
                      className="w-full pl-12 pr-12 py-3.5 rounded-xl outline-none bg-transparent text-slate-800 placeholder:text-slate-400"
                    />
                    <button type="button" onClick={() => setShow({ ...show, password: !show.password })} className="absolute right-4 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-700 transition">
                      {show.password ? <EyeOff size={19} /> : <Eye size={19} />}
                    </button>
                  </div>
                  {errors.password && <p className="text-red-500 text-sm mt-2">{errors.password}</p>}
                </div>

                <div>
                  <label className="block mb-2 text-sm font-semibold text-slate-700">Confirm New Password</label>
                  <div className={`relative rounded-xl border transition-all ${errors.confirm ? "border-red-400 ring-2 ring-red-100" : "border-slate-300 focus-within:border-blue-600 focus-within:ring-4 focus-within:ring-blue-100"}`}>
                    <Lock size={18} className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400" />
                    <input
                      type={show.confirm ? "text" : "password"}
                      placeholder="Re-enter new password"
                      value={form.confirm}
                      onChange={(e) => setForm({ ...form, confirm: e.target.value })}
                      className="w-full pl-12 pr-12 py-3.5 rounded-xl outline-none bg-transparent text-slate-800 placeholder:text-slate-400"
                    />
                    <button type="button" onClick={() => setShow({ ...show, confirm: !show.confirm })} className="absolute right-4 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-700 transition">
                      {show.confirm ? <EyeOff size={19} /> : <Eye size={19} />}
                    </button>
                  </div>
                  {errors.confirm && <p className="text-red-500 text-sm mt-2">{errors.confirm}</p>}
                </div>

                <button
                  type="submit"
                  disabled={loading}
                  className="w-full bg-blue-600 hover:bg-blue-700 active:scale-[0.99] transition-all duration-200 text-white rounded-xl py-3.5 font-semibold shadow-lg shadow-blue-200 disabled:opacity-70 disabled:cursor-not-allowed flex justify-center items-center gap-3"
                >
                  {loading ? (
                    <>
                      <Loader2 size={20} className="animate-spin" /> Resetting...
                    </>
                  ) : (
                    <>
                      Reset Password <ArrowRight size={18} />
                    </>
                  )}
                </button>
              </form>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}